import type { FC } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import OtpVerificationStep from './OtpVerificationStep';
import PhoneInputStep from './PhoneInputStep';
import { verificationModalContainerStyles, verificationModalOverlayStyles } from './styled';
import type { PhoneVerificationModalProps } from './types';
import { usePhoneVerification } from './usePhoneVerification';

const PhoneVerificationModal: FC<PhoneVerificationModalProps> = ({ isOpen, onVerificationComplete }) => {
	const modalRef = useRef<HTMLDivElement>(null);

	const {
		step,
		phoneNumber,
		setPhoneNumber,
		deliveryMethod,
		setDeliveryMethod,
		countdown,
		resendCooldown,
		isLoading,
		error,
		isExpired,
		sendCode,
		verifyCode,
		resendCode,
		changePhone,
	} = usePhoneVerification(onVerificationComplete);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (e.key === 'Escape' && step === 'phone') {
				e.preventDefault();
			}
		},
		[step]
	);

	useEffect(() => {
		if (!isOpen) return;

		document.addEventListener('keydown', handleKeyDown);
		document.body.style.overflow = 'hidden';

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			document.body.style.overflow = '';
		};
	}, [isOpen, handleKeyDown]);

	useEffect(() => {
		if (!isOpen || !modalRef.current) {
			return;
		}

		const focusableElements = modalRef.current.querySelectorAll(
			'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
		);

		const firstElement = focusableElements[0] as HTMLElement;
		const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

		const handleTabKey = (e: KeyboardEvent) => {
			if (e.key !== 'Tab') return;

			if (e.shiftKey) {
				if (document.activeElement === firstElement) {
					e.preventDefault();
					lastElement?.focus();
				}
			} else {
				if (document.activeElement === lastElement) {
					e.preventDefault();
					firstElement?.focus();
				}
			}
		};

		document.addEventListener('keydown', handleTabKey);
		return () => document.removeEventListener('keydown', handleTabKey);
	}, [isOpen, step]);

	if (!isOpen) {
		return null;
	}

	const modalId = 'phone-verification-modal';

	return (
		<div
			style={verificationModalOverlayStyles}
			role='presentation'
		>
			<div
				ref={modalRef}
				style={verificationModalContainerStyles}
				role='dialog'
				aria-modal='true'
				aria-labelledby={`${modalId}-title`}
			>
				{step === 'phone' ? (
					<PhoneInputStep
						phoneNumber={phoneNumber}
						setPhoneNumber={setPhoneNumber}
						deliveryMethod={deliveryMethod}
						setDeliveryMethod={setDeliveryMethod}
						onSendCode={sendCode}
						isLoading={isLoading}
						error={error}
					/>
				) : (
					<OtpVerificationStep
						phoneNumber={phoneNumber}
						deliveryMethod={deliveryMethod}
						onVerify={verifyCode}
						onResend={resendCode}
						onChangePhone={changePhone}
						isLoading={isLoading}
						error={error}
						countdown={countdown}
						resendCooldown={resendCooldown}
						isExpired={isExpired}
					/>
				)}
			</div>
		</div>
	);
};

export default PhoneVerificationModal;
export type { VerificationStorage } from './types';
export { checkExistingVerification } from './usePhoneVerification';
